import React from 'react';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { IAttendanceDeductionPreview } from '../../../services/technician';
import { POLICY_HINTS } from '../../../utils/attendancePolicy';

type Props = {
  preview: IAttendanceDeductionPreview;
};

const formatDays = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

const Row = ({
  label,
  value,
  hint,
  tag,
  bold,
  valueColor,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tag?: string;
  bold?: boolean;
  valueColor?: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      py: 0.4,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography
        variant="caption"
        color={bold ? '#f3f4f6' : '#9ca3af'}
        sx={{
          fontSize: '0.72rem',
          fontWeight: bold ? 700 : 400,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      {tag && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.6rem',
            color: '#6b7280',
            border: '1px solid #333',
            borderRadius: '4px',
            px: 0.5,
            lineHeight: 1.5,
          }}
        >
          {tag}
        </Typography>
      )}
      {hint && (
        <Tooltip title={hint} arrow>
          <InfoOutlined sx={{ fontSize: 12, color: '#6b7280' }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.75rem',
        fontWeight: 700,
        color: valueColor || '#f3f4f6',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Conversion step: value on the right, formula/context as a quiet second line. */
const StepRow = ({
  label,
  value,
  caption,
  hint,
}: {
  label: string;
  value: string | number;
  caption?: string;
  hint?: string;
}) => (
  <Box sx={{ py: 0.4 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="#9ca3af"
          sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlined sx={{ fontSize: 12, color: '#6b7280' }} />
          </Tooltip>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#f3f4f6' }}
      >
        {value}
      </Typography>
    </Box>
    {caption && (
      <Typography
        variant="caption"
        color="#6b7280"
        sx={{ display: 'block', fontSize: '0.64rem', lineHeight: 1.3 }}
      >
        {caption}
      </Typography>
    )}
  </Box>
);

const ColumnTitle = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    color="#6b7280"
    sx={{
      display: 'block',
      mb: 0.5,
      fontSize: '0.64rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}
  >
    {label}
  </Typography>
);

const Column = ({
  title,
  first,
  children,
}: {
  title: string;
  first?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      minWidth: 0,
      px: { xs: 0, lg: 2 },
      pl: first ? { xs: 0, lg: 0 } : undefined,
      borderLeft: { xs: 'none', lg: first ? 'none' : '1px solid #2a2a2a' },
    }}
  >
    <ColumnTitle label={title} />
    {children}
  </Box>
);

/**
 * Payroll statement for the visible month, rendered full-width below the
 * calendar grid: attendance counts → conversion steps → deductions → net
 * payable. Every number comes from the calendar API's deductionPreview
 * (same policy as the stored month summary), so an attendance edit
 * refreshes it with the calendar refetch. Deductions are shown as plain
 * day counts — no accounting minus signs. Old backends without the
 * `inputs` block get a compact totals-only fallback.
 */
export const MonthlyConversionSummary: React.FC<Props> = ({ preview }) => {
  const { conversions, deductions, netPayableDays, inputs } = preview;
  const leaveDeduction = deductions.leaveDeductionDays ?? 0;

  if (!inputs) {
    return (
      <Box
        sx={{
          borderTop: '1px solid #2a2a2a',
          backgroundColor: '#141414',
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="caption"
          color="#d1d5db"
          sx={{ display: 'block', mb: 0.5, fontWeight: 600, fontSize: '0.72rem' }}
        >
          Payroll conversion
        </Typography>
        <Box sx={{ maxWidth: 320 }}>
          <Row
            label="Absent days"
            value={formatDays(deductions.rawAbsentDays)}
          />
          <Row
            label="Late & half-day conversion"
            value={formatDays(deductions.conversionDeductionDays)}
            hint={POLICY_HINTS.HALF_DAY_CONVERSION}
          />
          <Row
            label="Total deduction"
            value={formatDays(deductions.totalDeductionDays)}
            bold
            valueColor="#E31E25"
          />
          <Row
            label="Net payable days"
            value={formatDays(netPayableDays)}
            bold
            valueColor="#22C55E"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderTop: '1px solid #2a2a2a',
        backgroundColor: '#141414',
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="#f3f4f6"
          sx={{ fontSize: '0.82rem' }}
        >
          Payroll conversion
        </Typography>
        <Typography variant="caption" color="#6b7280" sx={{ fontSize: '0.7rem' }}>
          {`Elapsed ${inputs.elapsedDays} of ${inputs.totalDaysInRange} days`}
          {inputs.pendingToday ? ' · today pending' : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1.05fr 1.25fr 1fr 0.85fr',
          },
          columnGap: { xs: 3, lg: 0 },
          rowGap: { xs: 1.5, lg: 0 },
        }}
      >
        <Column title="Attendance" first>
          <Row label="Present" value={inputs.present} />
          <Row
            label="Late check-in"
            value={inputs.late}
            hint={POLICY_HINTS.LATE_CHECK_IN}
          />
          <Row
            label="Half day"
            value={inputs.halfDay}
            hint={POLICY_HINTS.HALF_DAY}
          />
          <Row label="Absent" value={inputs.absent} />
          <Row label="Leave" value={inputs.leave} />
          <Row
            label="Holiday"
            value={inputs.holiday}
            tag="paid"
            hint="Paid — stays in the payable base"
          />
          <Row
            label="Weekly off"
            value={inputs.weeklyOff}
            tag="paid"
            hint={POLICY_HINTS.WEEKLY_OFF}
          />
        </Column>

        <Column title="Conversions">
          <StepRow
            label="Lates ÷ 3"
            value={conversions.lateHalfDayEquivalents}
            caption={
              conversions.residualLates > 0
                ? `${conversions.residualLates} late carried over`
                : '3 late check-ins count as half a day'
            }
            hint={POLICY_HINTS.LATE_CONVERSION}
          />
          <StepRow
            label="Half-day equivalents"
            value={conversions.totalHalfDayEquivalents}
            caption={`${conversions.lateHalfDayEquivalents} from lates + ${inputs.halfDay} worked half days`}
          />
          <StepRow
            label="Converted to absent"
            value={conversions.convertedAbsents}
            caption={
              conversions.leftoverHalfDay > 0
                ? 'plus half a day remaining'
                : '2 half-day equivalents = 1 absent'
            }
            hint={POLICY_HINTS.HALF_DAY_CONVERSION}
          />
        </Column>

        <Column title="Deductions · days">
          <Row label="Absent days" value={formatDays(deductions.rawAbsentDays)} />
          <Row
            label="Late & half-day conversion"
            value={formatDays(deductions.conversionDeductionDays)}
          />
          <Row
            label="Leave (unpaid)"
            value={formatDays(leaveDeduction)}
            hint="Full-day leave deducts 1 day, half-day leave 0.5"
          />
          <Divider sx={{ borderColor: '#2a2a2a', my: 0.5 }} />
          <Row
            label="Total deduction"
            value={formatDays(deductions.totalDeductionDays)}
            bold
            valueColor="#E31E25"
          />
        </Column>

        <Column title="Net payable">
          <Box sx={{ pt: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                sx={{
                  fontSize: '1.7rem',
                  fontWeight: 800,
                  color: '#22C55E',
                  lineHeight: 1.1,
                }}
              >
                {formatDays(netPayableDays)}
              </Typography>
              <Typography variant="caption" color="#9ca3af" sx={{ fontSize: '0.72rem' }}>
                of {inputs.elapsedDays} days
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="#6b7280"
              sx={{ display: 'block', mt: 0.5, fontSize: '0.66rem', lineHeight: 1.4 }}
            >
              {`${inputs.elapsedDays} elapsed − ${formatDays(
                deductions.totalDeductionDays,
              )} deducted. Weekly offs and holidays are paid.`}
            </Typography>
          </Box>
        </Column>
      </Box>
    </Box>
  );
};

export default MonthlyConversionSummary;
